import type {ActionResponse, ActionResponseWithText, Auth, ExceptionActionResponse, FinishedActionResponse, FinishedSaveResponse, LabeledIdentified, Loan, LoanRequestFormActionResponse, PayloadActionResponse, RedirectionResponse, UUID, User, ViewableFile} from 'src/typings';
import type {ModalWindowTypeToComponentMap} from './types';
import type {DiscountRequestModalModel} from 'src/modals/discount-request-modal/types';
import type {UserFilesUploadModalModel} from 'src/modals/user-files-upload-modal/types';
import type {FilesSelectionModalModel} from 'src/modals/files-selection-modal/types';
import type {DiagramNodeDetailsModalModel} from 'src/modals/diagram-node-details-modal/types';
import type {SpecialSymbolsKeyboardModalModel} from 'src/modals/special-symbols-keyboard-modal/types';
import type {SearchSelectionModalModel, SelectableItem} from 'src/modals/search-selection-modal/types';
import type {SettingsModalModel} from 'src/modals/settings-modal/types';
import type {UserPreferencesModalModel} from 'src/modals/user-preferences-modal/types';
import type {PasswordQuestionModalModel} from 'src/modals/password-question/types';
import type {ConfirmationDialogModalModel} from 'src/modals/confirmation-dialog/types';
import type {UniversalFormModalModel} from 'src/modals/universal-form-modal/types';
import type {RegistrationOptionsModalModel} from 'src/modals/registration-options-modal/types';
import type {RecordEditationModalModel} from 'src/modals/record-editation-modal/types';
import type {LoginView} from 'shared/login/kp-login/types';
import type {LoanSelectionModalModel} from 'src/modals/loan-selection-modal/types';
import type {CatalogueSelectionModalModel} from 'src/modals/catalogue-selection-modal/types';
import type {AuthPairingResponse} from 'shared/login/account-pairing-modal/types';
import type {MultipleRenewalResponse} from 'src/modals/finished-renewal-composite-response-modal/types';
import type {GoToResponse} from 'src/modals/go-to-response-modal/types';
import type {LoanReadyNoticeToSendQuestion} from 'src/modals/loan-ready-notice-to-send-question-modal/types';
import type {PaymentGatewayRedirectModalModel, PayRequestFormPackage} from 'src/features/payment/types';
import type {SubmittingFormActionModel} from 'src/modals/submitting-form-modal/types';
import type {AuthorityField8EditorModalModel} from 'src/features/record-editation/field7-and-field8-editors/authority-field8-editor-modal/types';
import type {Field7EditorModalModel} from 'src/features/record-editation/field7-and-field8-editors/field7-editor-modal/types';
import type {Field8EditorModalModel} from 'src/features/record-editation/field7-and-field8-editors/field8-editor-modal/types';
import type {FondFieldRelationTableModalModel} from 'src/modals/fond-field-relation-table-modal/types';
import type {PrintPageResponse} from 'src/modals/print-page/types';
import KpRegistrationOptionsModal from 'src/modals/registration-options-modal/KpRegistrationOptionsModal.svelte';
import KpRecordEditationModal from 'src/modals/record-editation-modal/KpRecordEditationModal.svelte';
import LoginFormModal from 'shared/login/login-form-modal/LoginFormModal.svelte';
import KpLoanSelectionModal from 'src/modals/loan-selection-modal/KpLoanSelectionModal.svelte';
import KpField8EditorModal from 'src/features/record-editation/field7-and-field8-editors/field8-editor-modal/KpField8EditorModal.svelte';
import KpField7EditorModal from 'src/features/record-editation/field7-and-field8-editors/field7-editor-modal/KpField7EditorModal.svelte';
import KpAuthorityField8EditorModal from 'src/features/record-editation/field7-and-field8-editors/authority-field8-editor-modal/KpAuthorityField8EditorModal.svelte';
import KpCatalogueSelectionModal from 'src/modals/catalogue-selection-modal/KpCatalogueSelectionModal.svelte';
import KpConfirmationDialogModal from 'src/modals/confirmation-dialog/KpConfirmationDialogModal.svelte';
import KpAccountPairingResponseModal from 'shared/login/account-pairing-modal/KpAccountPairingResponseModal.svelte';
import KpDepartmentCreatedModal from 'src/modals/department-created-modal/KpDepartmentCreatedModal.svelte';
import KpDiagramNodeDetailsModal from 'src/modals/diagram-node-details-modal/KpDiagramNodeDetailsModal.svelte';
import KpDiscountRequestModal from 'src/modals/discount-request-modal/KpDiscountRequestModal.svelte';
import KpExceptionModal from 'src/modals/exception-modal/KpExceptionModal.svelte';
import KpFilesSelectionModal from 'src/modals/files-selection-modal/KpFilesSelectionModal.svelte';
import KpFinishedRenewalCompositeResponseModal from 'src/modals/finished-renewal-composite-response-modal/KpFinishedRenewalCompositeResponseModal.svelte';
import KpGoToResponseModal from 'src/modals/go-to-response-modal/KpGoToResponseModal.svelte';
import KpLoanReadyNoticeQuestionToSendModal from 'src/modals/loan-ready-notice-to-send-question-modal/KpLoanReadyNoticeQuestionToSendModal.svelte';
import KpLoanRequestForm from 'src/modals/loan-request-form/KpLoanRequestForm.svelte';
import KpFinishedResponseModal from 'src/modals/finished-response-modal/KpFinishedResponseModal.svelte';
import KpPasswordQuestionModal from 'src/modals/password-question/KpPasswordQuestionModal.svelte';
import KpPaymentFormModal from 'src/features/payment/payment-form-modal/KpPaymentFormModal.svelte';
import KpPaymentGatewayRedirectModal from 'src/features/payment/payment-gateway-redirect-response-modal/KpPaymentGatewayRedirectModal.svelte';
import KpPrintPageModal from 'src/modals/print-page/KpPrintPageModal.svelte';
import KpRedirectionResponseModal from 'src/modals/redirection-response/KpRedirectionResponseModal.svelte';
import KpSearchSelectionModal from 'src/modals/search-selection-modal/KpSearchSelectionModal.svelte';
import KpSettingsModal from 'src/modals/settings-modal/KpSettingsModal.svelte';
import KpSpecialSymbolsKeyboardModal from 'src/modals/special-symbols-keyboard-modal/KpSpecialSymbolsKeyboardModal.svelte';
import KpUniversalFormModal from 'src/modals/universal-form-modal/KpUniversalFormModal.svelte';
import KpUserActivationRequiredModal from 'src/modals/user-activation-required-modal/KpUserActivationRequiredModal.svelte';
import KpUserFilesUploadModal from 'src/modals/user-files-upload-modal/KpUserFilesUploadModal.svelte';
import KpUserPreferencesModal from 'src/modals/user-preferences-modal/KpUserPreferencesModal.svelte';
import SubmittingFormModal from 'src/modals/submitting-form-modal/SubmittingFormModal.svelte';
import KpFondFieldRelationTableModal from 'src/modals/fond-field-relation-table-modal/KpFondFieldRelationTableModal.svelte';

export const MODAL_WINDOW_TYPE_TO_COMPONENT_MAP: ModalWindowTypeToComponentMap = {
    finishedRenewalResponse: KpFinishedResponseModal,
    finishedResponse: KpFinishedResponseModal,
    finishedSaveResponse: KpFinishedResponseModal,
    richTextFinishedResponse: KpFinishedResponseModal,
    authorityField8Editor: KpAuthorityField8EditorModal,
    catalogueSelection: KpCatalogueSelectionModal,
    confirmationDialog: KpConfirmationDialogModal,
    diagramNodeDetails: KpDiagramNodeDetailsModal,
    discountRequest: KpDiscountRequestModal,
    field7Editor: KpField7EditorModal,
    field8Editor: KpField8EditorModal,
    filesSelection: KpFilesSelectionModal,
    loanSelection: KpLoanSelectionModal,
    loginForm: LoginFormModal,
    passwordQuestion: KpPasswordQuestionModal,
    recordEditation: KpRecordEditationModal,
    registrationOptions: KpRegistrationOptionsModal,
    searchSelection: KpSearchSelectionModal,
    settings: KpSettingsModal,
    specialSymbolsKeyboard: KpSpecialSymbolsKeyboardModal,
    universalForm: KpUniversalFormModal,
    userFilesUpload: KpUserFilesUploadModal,
    userPreferences: KpUserPreferencesModal,
    authPairingResponse: KpAccountPairingResponseModal,
    departmentCreated: KpDepartmentCreatedModal,
    exception: KpExceptionModal,
    finishedRenewalCompositeResponse: KpFinishedRenewalCompositeResponseModal,
    goToResponse: KpGoToResponseModal,
    loanReadyNoticeToSendQuestion: KpLoanReadyNoticeQuestionToSendModal,
    loanRequestForm: KpLoanRequestForm,
    paymentForm: KpPaymentFormModal,
    paymentGatewayRedirectResponse: KpPaymentGatewayRedirectModal,
    printPage: KpPrintPageModal,
    redirectionResponse: KpRedirectionResponseModal,
    userActivationRequired: KpUserActivationRequiredModal,
    submittingForm: SubmittingFormModal,
    fondFieldRelationTable: KpFondFieldRelationTableModal
};

export interface ModalWindowTypeToModelMap {
    finishedRenewalResponse: FinishedActionResponse,
    finishedResponse: FinishedActionResponse,
    finishedSaveResponse: FinishedActionResponse,
    richTextFinishedResponse: FinishedActionResponse,
    authorityField8Editor: AuthorityField8EditorModalModel,
    catalogueSelection: CatalogueSelectionModalModel,
    confirmationDialog: ConfirmationDialogModalModel,
    diagramNodeDetails: DiagramNodeDetailsModalModel,
    discountRequest: DiscountRequestModalModel,
    field7Editor: Field7EditorModalModel,
    field8Editor: Field8EditorModalModel,
    filesSelection: FilesSelectionModalModel,
    loanSelection: LoanSelectionModalModel,
    loginForm: LoginView,
    passwordQuestion: PasswordQuestionModalModel,
    recordEditation: RecordEditationModalModel,
    registrationOptions: RegistrationOptionsModalModel,
    searchSelection: SearchSelectionModalModel,
    settings: SettingsModalModel,
    specialSymbolsKeyboard: SpecialSymbolsKeyboardModalModel,
    universalForm: UniversalFormModalModel,
    userFilesUpload: UserFilesUploadModalModel,
    userPreferences: UserPreferencesModalModel,
    authPairingResponse: AuthPairingResponse,
    departmentCreated: void;
    exception: ExceptionActionResponse;
    finishedRenewalCompositeResponse: MultipleRenewalResponse;
    goToResponse: GoToResponse;
    loanReadyNoticeToSendQuestion: LoanReadyNoticeToSendQuestion;
    loanRequestForm: LoanRequestFormActionResponse;
    paymentForm: PayRequestFormPackage;
    paymentGatewayRedirectResponse: PaymentGatewayRedirectModalModel;
    printPage: PrintPageResponse;
    redirectionResponse: RedirectionResponse;
    userActivationRequired: void;
    submittingForm: SubmittingFormActionModel;
    fondFieldRelationTable: FondFieldRelationTableModalModel;
}

export interface ModalWindowTypeToModalReturnTypeMap {
    finishedRenewalResponse: ActionResponse,// acknowledge
    finishedResponse: ActionResponse, // acknowledge
    finishedSaveResponse: ActionResponse, // acknowledge
    richTextFinishedResponse: ActionResponse, // acknowledge
    authorityField8Editor: PayloadActionResponse<string>,
    catalogueSelection: PayloadActionResponse<string>,
    confirmationDialog: ActionResponse, // acknowledge
    diagramNodeDetails: void, // cancel
    discountRequest: ActionResponseWithText | FinishedSaveResponse<User>,
    field7Editor: PayloadActionResponse<string>,
    field8Editor: PayloadActionResponse<string>,
    filesSelection: PayloadActionResponse<ViewableFile[]>,
    loanSelection: PayloadActionResponse<Loan>,
    loginForm: PayloadActionResponse<Auth>,
    passwordQuestion: PayloadActionResponse<string>;
    recordEditation: PayloadActionResponse<LabeledIdentified<UUID>>,
    registrationOptions: void; // cancel
    searchSelection: PayloadActionResponse<SelectableItem>;
    settings: void; // cancel
    specialSymbolsKeyboard: ActionResponse, // acknowledge
    universalForm: any, // custom mapper fn
    userFilesUpload: PayloadActionResponse<string[]>,
    userPreferences: void, // cancel
    authPairingResponse: void, // nothing
    departmentCreated: void; // cancel - but I want to change it
    exception: ActionResponse; // acknowledge
    finishedRenewalCompositeResponse: ActionResponse; // acknowledge
    goToResponse: void; // nothing
    loanReadyNoticeToSendQuestion: ActionResponse;
    loanRequestForm: ActionResponseWithText;
    paymentForm: ActionResponse;
    paymentGatewayRedirectResponse: void; // cancel
    printPage: ActionResponse; // acknowledge
    redirectionResponse: ActionResponse;
    userActivationRequired: void; // cancel - WHY?
    submittingForm: ActionResponse | string;
    fondFieldRelationTable: void;
}